import {
  Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, signal
} from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { Subject, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { GroupService } from '../../services/group.service';
import { SampleGroupField } from '../../models/sample-group-field';
import { parseBoundary } from '../../utils/geo-utils';
import {
  DEFAULT_OVERLAP_METRES, clusterCentre, clusterOverlaps, offsetPosition, spreadOffset
} from './sample-group-overlap';
import {
  SampleLabelMode, buildSamplePinMarker, paintSamplePinSelection, sampleBadgeText
} from './sample-pin-marker';

/** One sample sitting on a pin. A pin can carry several. */
export interface SampleGroupSample {
  sampleId: number | null;
  name: string;
  depth: number | null;
}

/**
 * A physical pin, with every sample recorded on it.
 *
 * The API returns one row per sample, so rows are folded back into pins here.
 * That distinction is the whole point: two samples on one pin (topsoil and
 * subsoil at the same spot) are normal, two pins in one place are the duplicate
 * we are hunting.
 */
export interface SampleGroupPin {
  pinId: number;
  lat: number;
  lng: number;
  fieldId: number;
  fieldName: string;
  pinTypeId: number;
  isCompleted: boolean;
  samples: SampleGroupSample[];
}

/** Re-exported so templates and the parent keep importing it from the component. */
export type LabelMode = SampleLabelMode;

/** Below this zoom the labels are unreadable, so only the pins draw. */
const LABEL_MIN_ZOOM = 14;

@Component({
  selector: 'app-sample-group-map',
  templateUrl: './sample-group-map.component.html',
  standalone: false
})
export class SampleGroupMapComponent implements OnChanges, OnDestroy {
  @ViewChild('map') map: GoogleMap;

  @Input() groupId: number;
  /** Rows the parent has loaded, used to name pins until the API returns names. */
  @Input() samples: any[] = [];
  /** Pin selected elsewhere on the screen, so the map follows the table. */
  @Input() selectedPinId: number | null = null;

  @Output() pinSelected = new EventEmitter<SampleGroupPin>();
  /** Number of samples sitting on a pin that shares its position with another pin. */
  @Output() overlapsChanged = new EventEmitter<number>();

  pins = signal<SampleGroupPin[]>([]);
  loading = signal(false);
  labelMode = signal<LabelMode>('name');
  showOverlaps = signal(true);
  spreadStacked = signal(false);
  labelsVisible = signal(true);

  /** Pre-built per render pass; never computed from a template expression. */
  markers: { position: google.maps.LatLngLiteral; content: HTMLElement; pinId: number }[] = [];
  polygons: { id: number; paths: google.maps.LatLngLiteral[]; name: string }[] = [];
  overlapCircles: { centre: google.maps.LatLngLiteral; radius: number }[] = [];

  mapCenter: google.maps.LatLngLiteral = { lat: -28, lng: 25 };
  mapZoom = 6;
  mapOptions: google.maps.MapOptions = {
    mapTypeId: 'satellite',
    mapId: 'DEMO_MAP_ID',
    zoomControl: false,     // custom cluster bottom-right, same as the maps module
    cameraControl: false,
    rotateControl: false,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false
  };

  fieldPolygonOptions: google.maps.PolygonOptions = {
    strokeColor: environment.google.mapConfig.polygonStrokeColour,
    strokeWeight: 2,
    fillColor: environment.google.mapConfig.polygonStrokeColour,
    fillOpacity: 0.08,
    clickable: false
  };

  overlapCircleOptions: google.maps.CircleOptions = {
    strokeColor: '#FFD500',
    strokeWeight: 2,
    fillColor: '#FFD500',
    fillOpacity: 0.18,
    clickable: false
  };

  private markerContent = new Map<number, HTMLElement>();
  /** Last value handed to the parent, so an unchanged count is not re-emitted. */
  private lastOverlapCount: number | null = null;
  private fitBounds$ = new Subject<google.maps.LatLngBounds>();
  private destroy$ = new Subject<void>();

  constructor(private groupService: GroupService) {
    // fitBounds must not run in an effect(): it re-enters change detection and
    // loops. Subject plus a macrotask is the pattern the maps module uses.
    this.fitBounds$.pipe(takeUntil(this.destroy$)).subscribe(bounds => {
      setTimeout(() => this.map?.googleMap?.fitBounds(bounds, 48), 0);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['groupId'] && this.groupId) this.load();
    if (changes['samples'] && !changes['samples'].firstChange) this.applySampleNames();
    if (changes['selectedPinId']) this.paintSelection();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private load(): void {
    this.loading.set(true);

    this.groupService.getHistoricPinDetailsForGriidingBySampleGroupId(this.groupId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(rows => {
        this.pins.set(this.foldRowsIntoPins(rows || []));
        this.applySampleNames();
        this.rebuild();
        this.loading.set(false);
      });

    this.groupService.getSampleGroupFields(this.groupId)
      .pipe(takeUntil(this.destroy$), catchError(() => of([] as SampleGroupField[])))
      .subscribe(fields => {
        this.polygons = (fields || [])
          .map(field => ({
            id: field.fieldId,
            name: field.fieldName,
            paths: parseBoundary(field.boundry)
          }))
          .filter(polygon => polygon.paths.length > 0);
        this.rebuild();
      });
  }

  /**
   * The endpoint selects from pin_samples, so it returns one row per sample and
   * repeats the pin for every sample on it. Fold those rows back into one entry
   * per pin, otherwise a two-sample pin draws two markers on the same spot and
   * reads as a duplicate.
   */
  private foldRowsIntoPins(rows: any[]): SampleGroupPin[] {
    const byPinId = new Map<number, SampleGroupPin>();

    rows.filter(row => this.hasPosition(row)).forEach(row => {
      let pin = byPinId.get(row.id);
      if (!pin) {
        pin = {
          pinId: row.id,
          lat: Number(row.lat),
          lng: Number(row.lng),
          fieldId: row.fieldId,
          fieldName: row.fieldName ?? '',
          pinTypeId: row.pinTypeId,
          isCompleted: !!row.isCompleted,
          samples: []
        };
        byPinId.set(row.id, pin);
      }
      pin.samples.push({
        sampleId: row.sampleId ?? null,
        name: row.sampleName ?? '',
        depth: row.sampleDepth ?? null
      });
      // A pin counts as captured once any sample on it is captured.
      pin.isCompleted = pin.isCompleted || !!row.isCompleted;
    });

    return Array.from(byPinId.values());
  }

  /**
   * pins.lat and pins.lng are text columns, so nothing at the database level
   * stops an empty string or a nonsense value. Number('') is 0, which would
   * plot in the Gulf of Guinea, and the API substitutes -1 for null.
   */
  private hasPosition(row: any): boolean {
    const rawLat = row?.lat, rawLng = row?.lng;
    if (rawLat == null || rawLng == null) return false;
    if (String(rawLat).trim() === '' || String(rawLng).trim() === '') return false;

    const lat = Number(rawLat);
    const lng = Number(rawLng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
    if (lat === -1 && lng === -1) return false;
    if (lat === 0 && lng === 0) return false;
    return Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
  }

  /**
   * The pins endpoint does not return sample names yet, so fill them in from the
   * rows the parent has loaded. Falls away on its own once the API returns
   * sampleName, because that value is kept in preference.
   */
  private applySampleNames(): void {
    if (!this.samples?.length) return;
    const byPinId = new Map<number, any[]>();
    this.samples.forEach(sample => {
      const pinId = sample?.pin?.id;
      if (pinId == null) return;
      if (!byPinId.has(pinId)) byPinId.set(pinId, []);
      byPinId.get(pinId).push(sample);
    });
    if (!byPinId.size) return;

    this.pins.update(pins => pins.map(pin => {
      const loaded = byPinId.get(pin.pinId);
      if (!loaded?.length) return pin;
      return {
        ...pin,
        fieldName: pin.fieldName || loaded[0].field?.name || '',
        samples: pin.samples.map((sample, index) => {
          const match = loaded.find(row => row.id === sample.sampleId) ?? loaded[index];
          if (!match) return sample;
          return {
            sampleId: sample.sampleId ?? match.id ?? null,
            name: sample.name || match.name || '',
            depth: sample.depth ?? match.soil?.sampleDepth ?? null
          };
        })
      };
    }));
    this.rebuild();
  }

  // ---------------------------------------------------------------- rendering

  private rebuild(): void {
    const pins = this.pins();
    const clusters = this.showOverlaps()
      ? clusterOverlaps(pins.map(pin => ({ id: pin.pinId, lat: pin.lat, lng: pin.lng })), DEFAULT_OVERLAP_METRES)
      : [];
    const position = this.positionResolver(clusters);

    this.markerContent.clear();
    this.markers = pins.map(pin => ({
      position: position(pin),
      content: this.buildMarker(pin),
      pinId: pin.pinId
    }));

    this.overlapCircles = clusters.map(group => ({
      centre: clusterCentre(group),
      radius: this.spreadStacked() ? 16 : DEFAULT_OVERLAP_METRES
    }));

    // No Array.flat(): this project's tsconfig lib predates es2019.
    const overlappingPinIds = new Set<number | string>();
    clusters.forEach(group => group.forEach(entry => overlappingPinIds.add(entry.id)));
    const affectedSamples = pins
      .filter(pin => overlappingPinIds.has(pin.pinId))
      .reduce((total, pin) => total + Math.max(1, pin.samples.length), 0);
    this.emitOverlapCount(affectedSamples);

    this.paintSelection();
    this.fitToPins();
  }

  /**
   * Report the overlap count without writing to the parent mid-check.
   *
   * `rebuild()` is reachable from `ngOnChanges`, and the parent binds this to a
   * counter it reads higher up its own template than the map itself. Emitting
   * synchronously from there changes a value that has already been checked in
   * the same pass, which is ExpressionChangedAfterItHasBeenCheckedError in dev
   * mode. A macrotask moves the write to the next pass, the same reason and the
   * same pattern as the fitBounds call in the constructor.
   *
   * The equality guard is not an optimisation. `load()` rebuilds, then patches
   * sample names and rebuilds again, so one page load would otherwise emit the
   * same number twice and flicker the header pill.
   */
  private emitOverlapCount(count: number): void {
    if (count === this.lastOverlapCount) return;
    this.lastOverlapCount = count;
    setTimeout(() => this.overlapsChanged.emit(count), 0);
  }

  /**
   * Where each pin draws. Two pins on an identical coordinate hide each other,
   * so when spreading is on the members of a cluster fan out.
   */
  private positionResolver(
    clusters: { id: number | string; lat: number; lng: number }[][]
  ): (pin: SampleGroupPin) => google.maps.LatLngLiteral {
    if (!this.spreadStacked() || !clusters.length) {
      return pin => ({ lat: pin.lat, lng: pin.lng });
    }

    const offsets = new Map<number | string, google.maps.LatLngLiteral>();
    clusters.forEach(group => {
      group.forEach((entry, index) => {
        const { east, north } = spreadOffset(index, group.length);
        offsets.set(entry.id, offsetPosition({ lat: entry.lat, lng: entry.lng }, east, north));
      });
    });
    return pin => offsets.get(pin.pinId) ?? { lat: pin.lat, lng: pin.lng };
  }

  /** The badge as one cached element. Rebuilt only when inputs change. */
  private buildMarker(pin: SampleGroupPin): HTMLElement {
    const names = pin.samples.map(sample => sample.name);
    // The title ignores the label mode so a pin is still identifiable with
    // labels switched off, or below the zoom at which they draw.
    const title = sampleBadgeText(names, 'name');
    const element = buildSamplePinMarker(
      sampleBadgeText(names, this.labelsVisible() ? this.labelMode() : 'none'),
      title,
      pin.pinId === this.selectedPinId
    );

    this.markerContent.set(pin.pinId, element);
    return element;
  }

  /** Selection is a style change on the cached element, not a marker rebuild. */
  private paintSelection(): void {
    this.markerContent.forEach((element, pinId) => {
      paintSamplePinSelection(element, pinId === this.selectedPinId);
    });
  }

  private fitToPins(): void {
    const pins = this.pins();
    if (!pins.length && !this.polygons.length) return;

    const bounds = new google.maps.LatLngBounds();
    pins.forEach(pin => bounds.extend(new google.maps.LatLng(pin.lat, pin.lng)));
    this.polygons.forEach(polygon =>
      polygon.paths.forEach(point => bounds.extend(new google.maps.LatLng(point.lat, point.lng))));
    this.fitBounds$.next(bounds);
  }

  // ------------------------------------------------------------------ actions

  onMarkerClick(pinId: number): void {
    const pin = this.pins().find(candidate => candidate.pinId === pinId);
    if (pin) this.pinSelected.emit(pin);
  }

  onLabelModeChange(mode: LabelMode): void {
    this.labelMode.set(mode);
    this.rebuild();
  }

  toggleOverlaps(): void {
    this.showOverlaps.update(value => !value);
    this.rebuild();
  }

  toggleSpread(): void {
    this.spreadStacked.update(value => !value);
    this.rebuild();
  }

  /** Labels switch off when zoomed out far enough that they would be unreadable. */
  onZoomChanged(): void {
    const zoom = this.map?.googleMap?.getZoom() ?? 0;
    const visible = zoom >= LABEL_MIN_ZOOM;
    if (visible !== this.labelsVisible()) {
      this.labelsVisible.set(visible);
      this.rebuild();
    }
  }

  zoomIn(): void { this.map?.googleMap?.setZoom((this.map.googleMap.getZoom() ?? 6) + 1); }
  zoomOut(): void { this.map?.googleMap?.setZoom((this.map.googleMap.getZoom() ?? 6) - 1); }
  fit(): void { this.fitToPins(); }

  trackByPinId(_index: number, marker: { pinId: number }): number { return marker.pinId; }
  trackByFieldId(_index: number, polygon: { id: number }): number { return polygon.id; }
  trackByIndex(index: number): number { return index; }
}
